import { ObjectId } from 'bson';
import { action, computed, makeObservable } from 'mobx';
import type { Moment } from 'moment';

import { concatPath, logError, moment } from '@feathr/hooks';
import type { IBaseAttributes, TConstraints } from '@feathr/rachis';
import { Collection, DisplayModel, isWretchError, wretch } from '@feathr/rachis';

export type TSegmentExportState =
  | 'pending'
  | 'exporting'
  | 'stopping'
  | 'ready'
  | 'failed'
  | 'stopped'
  | 'expired';

export type TSegmentExportType = 'email_activity' | 'persons' | 'breadcrumb';

export type TSegmentExportPhase = 'enumerating' | 'exporting';

/* Mirrors anhinga's `ACTIVE_EXPORT_STATES`. Excludes `stopping`: the BE 409s a second stop. */
const ACTIVE_STATES = new Set<TSegmentExportState>(['pending', 'exporting']);

/*
 * Upper bound on ids sent in one poll request, keeping the query string well
 * inside proxy URL limits (~24 characters per ObjectId). An account with more
 * concurrent exports than this refreshes the first `MAX_POLLED_EXPORT_IDS` on a
 * given tick; the remainder are picked up on later ticks as earlier rows reach
 * a terminal state and drop out of the set.
 */
export const MAX_POLLED_EXPORT_IDS = 50;

export interface ISegmentExport extends IBaseAttributes {
  person_segment: string;
  account: string;
  export_type: TSegmentExportType;
  state: TSegmentExportState;
  phase: TSegmentExportPhase | null;
  params: Record<string, unknown>;
  email: string;
  name?: string;
  created_by: string;
  is_archived: boolean;
  date_created: string | null;
  date_started: string | null;
  date_finished: string | null;
  rows_written: number;
  files_written: number;
  file_size: number | null;
  current_chunk_index: number;
  total_chunks: number | null;
  /** Percent complete, 0-100, as reported by the BE. Null until the export starts. */
  progress: number | null;
  error: string | null;
}

export class SegmentExport extends DisplayModel<ISegmentExport> {
  public readonly className = 'SegmentExport';

  public get constraints(): TConstraints<ISegmentExport> {
    return {
      email: {
        presence: { allowEmpty: false },
      },
      person_segment: {
        presence: { allowEmpty: false },
      },
      export_type: {
        presence: { allowEmpty: false },
        inclusion: ['email_activity', 'persons', 'breadcrumb'],
      },
    };
  }

  constructor(attributes: Partial<ISegmentExport> = {}) {
    super(attributes);

    makeObservable(this);
  }

  public override getDefaults(): Partial<ISegmentExport> {
    return {
      is_archived: false,
      rows_written: 0,
      files_written: 0,
      current_chunk_index: 0,
    };
  }

  public getItemUrl(pathSuffix?: string): string {
    return concatPath(`/data/exports/${this.id}`, pathSuffix);
  }

  @computed
  public get name(): string {
    return this.get('name') ?? '';
  }

  @computed
  public get dateCreated(): Moment {
    const dateCreated = this.get('date_created');
    if (dateCreated) {
      return moment.utc(dateCreated);
    }
    return moment.utc(new ObjectId(this.id).getTimestamp());
  }

  @computed
  public get dateFinished(): Moment | null {
    const dateFinished = this.get('date_finished');
    return dateFinished ? moment.utc(dateFinished) : null;
  }

  @computed
  public get downloadUrl(): string {
    if (this.collection) {
      return `${this.collection.url()}${this.id}/download`;
    }
    return '';
  }

  /** Whether a Stop action applies — true only while the export is in progress. */
  @computed
  public get canStop(): boolean {
    return ACTIVE_STATES.has(this.get('state'));
  }

  /** Whether a stop is outstanding and the running job has yet to observe it. */
  @computed
  public get isStopping(): boolean {
    return this.get('state') === 'stopping';
  }

  /** Whether the export has finished writing files and can be downloaded. */
  @computed
  public get canDownload(): boolean {
    return this.get('state') === 'ready';
  }

  /**
   * Whether the export can be archived. `is_archived` is delete-equivalent and
   * orthogonal to `state`, so any not-yet-archived export is archivable
   * regardless of where it sits in the state machine.
   */
  @computed
  public get canArchive(): boolean {
    return !this.get('is_archived');
  }

  /**
   * Whether a previously-ready export's download link has aged out. BE-emitted
   * (`state` flips to `expired`); the UI shows a disabled Download with an
   * explanatory tooltip.
   */
  @computed
  public get isExpired(): boolean {
    return this.get('state') === 'expired';
  }
}

export class SegmentExports extends Collection<SegmentExport> {
  public getClassName(): string {
    return 'segment-exports';
  }

  public getModel(attributes: Partial<ISegmentExport>): SegmentExport {
    return new SegmentExport(attributes);
  }

  /**
   * Silently refreshes the exports that are still in progress.
   *
   * Issues a single `id__in` GET for the ids currently in {@link ACTIVE_STATES}
   * and merges the response through `processListJSONResponse`. Because
   * `processJSONResponse` reuses the existing instance registered in
   * `modelsById`, the merge lands on the very objects every cached list URL
   * already holds — so one request refreshes the visible table, and any other
   * cached filter/sort/page combination, at once.
   *
   * Nothing on the cache entries is reassigned: `models` is not replaced and
   * `isPending` is never flipped, so consumers reading those observables (e.g.
   * the dashboard `Table`) render no loading state during the refetch.
   * Individual cells re-render only when the attributes they observe actually
   * change. Designed for short-interval polling (e.g. dashboard progress).
   *
   * The request is keyed on ids rather than `state__in` on purpose: a row that
   * has *just* left `pending`/`exporting` has to come back in the response for
   * the UI to learn it finished. Filtering on the active states would omit
   * exactly the transition the poll exists to observe.
   *
   * Only in-progress rows are fetched, so this does not pick up exports created
   * elsewhere while the page is open, and it does not refresh `pagination`.
   * Terminal rows cannot change, and the polling hook only runs while an active
   * row is already on screen, so neither costs the dashboard anything.
   *
   * Fetch failures are swallowed (the rows keep their last-known values and the
   * next poll tick retries) but reported to Sentry via `logError` so consistent
   * failures stay visible.
   */
  @action
  public async refreshActiveListings(): Promise<void> {
    /*
     * `models` is the union of every list this collection has loaded, so an
     * active row is refreshed no matter which cached URL surfaced it. Ephemeral
     * models are skipped: they hold a client-generated id the API has never
     * seen.
     */
    const activeIds = this.models
      .filter((model) => !model.isEphemeral && ACTIVE_STATES.has(model.get('state')))
      .map((model) => model.id)
      .slice(0, MAX_POLLED_EXPORT_IDS);

    if (!activeIds.length) {
      return;
    }

    const url = this.paramsToUrl({
      filters: { id__in: activeIds },
      pagination: { page_size: activeIds.length },
    });

    const response = await wretch<ISegmentExport>(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (isWretchError(response)) {
      logError({
        message: 'Silent refresh of segment exports listing failed',
        level: 'warning',
        extra: { url, error: response.error.message },
      });
      return;
    }

    /*
     * `skipUpdateApiCache` stays false so `updateApiCache` also re-seats each
     * returned model in every cached list that contains its id.
     */
    this.processListJSONResponse(response, false);
  }
}
